import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MessageCircle, MapPin, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Modernização de ERP",
  "Reforma Tributária",
  "Automação & RPA",
  "IA aplicada",
  "KPIs & BI",
  "Trilha Academy (Workshops)",
  "Diagnóstico geral",
  "Outro",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    interest: INTERESTS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Recebemos sua mensagem. Entraremos em contato em breve!");
      setForm({ name: "", email: "", company: "", role: "", phone: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error("Erro ao enviar. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="relative py-28 md:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: pitch + contacts */}
          <div className="lg:col-span-5">
            <p className="label-eyebrow">Vamos conversar</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight tracking-tight text-white">
              Diagnóstico <span className="gradient-text">sem custo</span> para começar.
            </h2>
            <p className="mt-6 text-lg text-[#B8C0D0] leading-relaxed">
              Conte um pouco do seu contexto atual e agendaremos uma conversa de 30 minutos para explorar oportunidades. Sem compromisso, sem vendedor.
            </p>

            <div className="mt-10 space-y-4" data-testid="contact-channels">
              <a
                href="https://wa.me/5511980331231?text=Ol%C3%A1%20David%2C%20quero%20conversar%20sobre%20consultoria%20em%20TI."
                target="_blank"
                rel="noreferrer"
                className="card-surface p-5 flex items-center gap-4 hover:border-cyan-400/40"
                data-testid="contact-whatsapp"
              >
                <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                  <MessageCircle size={20} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-mono-tech text-[10px] text-cyan-400 tracking-widest">// WHATSAPP</p>
                  <p className="text-white font-display text-lg">(11) 98033-1231</p>
                </div>
              </a>

              <a
                href="mailto:david.ponzenato@ponzena.com.br"
                className="card-surface p-5 flex items-center gap-4 hover:border-cyan-400/40"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                  <Mail size={20} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-mono-tech text-[10px] text-cyan-400 tracking-widest">// E-MAIL</p>
                  <p className="text-white font-display text-lg break-all">david.ponzenato@ponzena.com.br</p>
                </div>
              </a>

              <div className="card-surface p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                  <MapPin size={20} className="text-cyan-400" />
                </div>
                <div>
                  <p className="font-mono-tech text-[10px] text-cyan-400 tracking-widest">// ATUAÇÃO</p>
                  <p className="text-white font-display text-lg">Brasil &amp; LATAM · remoto/híbrido</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="card-surface p-8 md:p-10"
              data-testid="contact-form"
            >
              {done ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <CheckCircle2 size={56} className="text-cyan-400 mx-auto" />
                  <h3 className="font-display text-3xl text-white mt-6">Mensagem recebida!</h3>
                  <p className="text-[#B8C0D0] mt-3 max-w-md mx-auto">
                    Vamos analisar seu contexto e retornar em até 1 dia útil. Se preferir, chame agora pelo WhatsApp.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="btn-secondary mt-8"
                    type="button"
                    data-testid="contact-new-message"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="label-eyebrow">Formulário</p>
                    <h3 className="font-display text-3xl text-white mt-2">Fale conosco!</h3>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nome *" name="name" value={form.name} onChange={onChange} />
                    <Field label="E-mail *" name="email" type="email" value={form.email} onChange={onChange} />
                    <Field label="Empresa" name="company" value={form.company} onChange={onChange} />
                    <Field label="Cargo" name="role" value={form.role} onChange={onChange} />
                    <Field label="Telefone" name="phone" value={form.phone} onChange={onChange} />
                    <div className="flex flex-col">
                      <label className="label-eyebrow mb-2">Interesse</label>
                      <select
                        name="interest"
                        value={form.interest}
                        onChange={onChange}
                        className="bg-[#030B1E] border border-cyan-400/20 rounded-lg px-4 py-3 text-white focus:border-cyan-400 transition-colors"
                        data-testid="contact-interest"
                      >
                        {INTERESTS.map((i) => (
                          <option key={i} value={i} className="bg-[#030B1E]">
                            {i}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col">
                    <label className="label-eyebrow mb-2">Mensagem *</label>
                    <textarea
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      placeholder="Conte um pouco do seu contexto: ERP atual, principais dores, resultado que busca…"
                      className="bg-[#030B1E] border border-cyan-400/20 rounded-lg px-4 py-3 text-white focus:border-cyan-400 transition-colors resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {loading ? "Enviando..." : (<>Enviar mensagem <Send size={18} /></>)}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange }) {
  return (
    <div className="flex flex-col">
      <label className="label-eyebrow mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="bg-[#030B1E] border border-cyan-400/20 rounded-lg px-4 py-3 text-white focus:border-cyan-400 transition-colors"
        data-testid={`contact-field-${name}`}
      />
    </div>
  );
}
